import React, { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { styled } from '@mui/material';
import { Button, Checkbox, FormControlLabel, FormGroup, FormLabel, Grid, TextField } from '@mui/material';
import { isEmpty } from 'lodash';
import validate from 'validate.js';
import {
  ANALYST,
  COMPANY,
  COMPANY_DASHBOARD_ROLES,
  COMPANY_USER,
  COMPANY_USER_VALUE,
  FULL_ACCESS_VIEWER_VALUE,
  FUND,
  FUND_ADMIN,
  FUND_ADMIN_VALUE,
  LIMITED_VIEWER_VALUE,
  USER_ROLES,
} from 'common/constants/user';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ItemType } from 'pages/UserPermissions/common/types';
import { InviteUserLedgerFormState, InviteUserLedgerProps, NullableItemType } from './InviteUserLedger.props';
import CompanyFundSelect from '../CompanyFundSelect';
import { inviteUserLedgerClasses, InviteUserLedgerRoot } from './InviteUserLedger.styles';
import constraints from './InviteUserLedger.validations';



const InviteUserLedger = ({
  dialogUserData,
  handleDialogClose,
  handleAddUserToFirm,
  isCompanyInvite = false,
}: InviteUserLedgerProps) => {
  const [{ companyList, fundList }] = useStore() as unknown as UseStoreValues;

  const [formState, setFormState] = useState<InviteUserLedgerFormState>({
    isValid: false,
    values: {
      firstName: '',
      lastName: '',
      email: '',
    },
    errors: {},
    touched: {},
  });

  const [touched, setTouched] = useState<InviteUserLedgerFormState['touched']>({
    firstName: false,
    lastName: false,
    email: false,
  });

  const [roleType, setRoleType] = useState<number>(USER_ROLES[ANALYST].value);

  const [firmItem, setFirmItem] = useState<NullableItemType>({
    itemType: null,
    itemId: null,
  });

  const companyUserRoleInvalid
    = roleType === USER_ROLES[COMPANY_USER].value && !isCompanyInvite && (companyList?.length === 0 || !firmItem.itemId);

  const fundAdminRoleInvalid
    = roleType === USER_ROLES[FUND_ADMIN].value && (fundList?.length === 0 || !firmItem.itemId);

  const isSubmitButtonDisabled = !formState.isValid || companyUserRoleInvalid || fundAdminRoleInvalid;

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();
      const { name, checked } = event.target;
      const tmpValues = { ...formState.values, [name]: checked };
      const errors = validate(tmpValues, constraints);

      setFormState(prevFormState => ({
        ...prevFormState,
        values: tmpValues,
        errors,
        isValid: isEmpty(errors),
      }));

      setTouched({
        ...touched,
        [name]: true,
      });
    },
    [formState.values, touched]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();
      const { name, value } = event.target;
      const tmpValues = { ...formState.values, [name]: value };
      const errors = validate(tmpValues, constraints);

      setFormState(prevFormState => ({
        ...prevFormState,
        values: tmpValues,
        errors,
        isValid: isEmpty(errors),
      }));

      setTouched({
        ...touched,
        [name]: true,
      });
    },
    [formState.values, touched]
  );

  const handleRoleToggle = useCallback(({ value }: { value: number }) => {
    setFirmItem({ itemType: null, itemId: null });
    setRoleType(value);
    setFormState(prevFormState => ({
      ...prevFormState,
      values: {
        ...prevFormState.values,
        isPrimaryContact: false,
        sendActivationEmail: false,
      },
    }));
  }, []);

  const handleSelect = useCallback(({ itemType, itemId }: ItemType) => {
    if (itemId && itemType) {
      if ([COMPANY, FUND].includes(itemType)) {
        setFirmItem({ itemType, itemId });
      }
    }
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      const newUser = { ...formState.values, role_id: roleType };
      const role = {
        id: roleType,
        firmItem,
      };
      handleAddUserToFirm(newUser, role);
    },
    [formState.values, roleType, firmItem, handleAddUserToFirm]
  );

  if (!dialogUserData.open) {
    return null;
  }

  return (
    <InviteUserLedgerRoot onSubmit={handleSubmit} id="invite-user">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            First Name
          </FormLabel>
          <TextField
            autoFocus
            fullWidth
            placeholder="John"
            name="firstName"
            variant="outlined"
            value={formState.values.firstName ?? ''}
            onChange={handleChange}
            error={touched.firstName && !!formState.errors?.firstName}
            helperText={touched.firstName && formState.errors?.firstName?.[0]}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            Last Name
          </FormLabel>
          <TextField
            fullWidth
            placeholder="Doe"
            name="lastName"
            variant="outlined"
            value={formState.values.lastName ?? ''}
            onChange={handleChange}
            error={touched.lastName && !!formState.errors?.lastName}
            helperText={touched.lastName && formState.errors?.lastName?.[0]}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            Email
          </FormLabel>
          <TextField
            fullWidth
            placeholder="john@doe.com"
            name="email"
            variant="outlined"
            value={formState.values.email}
            onChange={handleChange}
            error={touched.email && !!formState.errors?.email}
            helperText={touched.email && formState.errors?.email?.[0]}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            Define Role
          </FormLabel>
          <FormGroup row>
            <div className={inviteUserLedgerClasses.radioButtonsGroup}>
              {Object.values(isCompanyInvite ? COMPANY_DASHBOARD_ROLES : USER_ROLES).map((r, index) => (
                <button
                  type="button"
                  key={r.value}
                  className={`${inviteUserLedgerClasses.button} ${roleType === Number(r.value) ? inviteUserLedgerClasses.buttonSelected : ''}`}
                  onClick={() => handleRoleToggle({ value: r.value })}>
                  {![FULL_ACCESS_VIEWER_VALUE, LIMITED_VIEWER_VALUE].includes(index) ? r.label.split(' ')[0] : r.label}
                </button>
              ))}
            </div>
          </FormGroup>
        </Grid>
        {isCompanyInvite && (
          <>
            {roleType === USER_ROLES[COMPANY_USER].value && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.values.isPrimaryContact}
                      onChange={handleCheckboxChange}
                      name="isPrimaryContact"
                      color="primary"
                    />
                  }
                  label="Primary Contact"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.values.sendActivationEmail || false}
                    onChange={handleCheckboxChange}
                    name="sendActivationEmail"
                    color="primary"
                  />
                }
                label="Send Activation Email"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          {[COMPANY_USER_VALUE, FUND_ADMIN_VALUE].includes(roleType) && !isCompanyInvite && (
            <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
              {`Select ${roleType === COMPANY_USER_VALUE ? COMPANY : FUND}`}
            </FormLabel>
          )}
          {roleType === FUND_ADMIN_VALUE && (
            <CompanyFundSelect
              value={firmItem.itemId as number}
              handleSelect={handleSelect}
              itemType={FUND}
              itemList={
                fundList?.map(fund => ({
                  id: fund.id as number,
                  name: fund.name ?? '',
                })) ?? []
              }
            />
          )}
          {!isCompanyInvite && roleType === COMPANY_USER_VALUE && (
            <CompanyFundSelect
              value={firmItem.itemId as number}
              handleSelect={handleSelect}
              itemType={COMPANY}
              itemList={
                companyList?.map(company => ({
                  id: company.company_id as number,
                  name: company.company_name ?? '',
                })) ?? []
              }
            />
          )}
        </Grid>
      </Grid>
      <div className={inviteUserLedgerClasses.buttonGroup}>
        <Button variant="outlined" color="primary" style={{ marginRight: '1rem' }} onClick={() => handleDialogClose()}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={isSubmitButtonDisabled} type="submit">
          Add User
        </Button>
      </div>
    </InviteUserLedgerRoot>
  );
};

export default InviteUserLedger;
